import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, BookOpen } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const API = `${window.location.origin}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then(r => { setPosts(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <main className="min-h-screen bg-[#0A0A0A]">
      <SEOHead
        title="Blog & Knowledge Base"
        description="Fragrance education, scent guides, and product knowledge from JS Fragrance. Learn how to choose, wear, and care for premium Eau de Parfum."
        url="https://jsfragrances.com/blog"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }]} />

      <header className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-8 text-center">
        <BookOpen className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.4} />
        <h1 className="text-4xl sm:text-5xl font-light text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Fragrance Knowledge
        </h1>
        <p className="text-white/50 text-sm font-sans max-w-2xl mx-auto">
          Guides, scent education, and stories from the JS Fragrance perfumery.
        </p>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-20">
        {loading ? (
          <div className="text-center py-16"><p className="text-white/40 font-sans text-sm">Loading…</p></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 border border-white/5 bg-white/[0.02]">
            <p className="text-white/50 font-sans text-sm">New articles coming soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map(p => (
              <article key={p.id} data-testid={`blog-card-${p.slug}`} className="group bg-[#141414] border border-white/5 hover:border-[#D4AF37]/40 transition-colors overflow-hidden">
                <Link to={`/blog/${p.slug}`} className="block">
                  {p.cover_image ? (
                    <div className="aspect-[16/10] overflow-hidden bg-[#0A0A0A]">
                      <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-gradient-to-br from-[#1C7D3F]/20 to-[#0A0A0A]" aria-hidden="true" />
                  )}
                  <div className="p-5">
                    {p.tags && p.tags[0] && (
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans mb-2">{p.tags[0]}</p>
                    )}
                    <h2 className="text-white text-lg font-medium mb-2 group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {p.title}
                    </h2>
                    {p.excerpt && <p className="text-white/50 text-sm font-sans mb-3 line-clamp-2">{p.excerpt}</p>}
                    <div className="flex items-center justify-between text-[11px] font-sans text-white/30">
                      <span>{formatDate(p.published_at || p.created_at)}</span>
                      <span className="flex items-center gap-1 text-white/50 group-hover:text-[#D4AF37] transition-colors">
                        Read <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
